import { useEffect, useMemo, useState } from "react"
import ProductService from "services/product"

const productService = new ProductService()

const PRODUCT_TOKEN = /\{\{\s*PRODUCT\s*:\s*([^}]+)\s*\}\}/gi
const PRODUCT_LIST_TOKEN = /\{\{\s*PRODUCT_LIST\s*(?::\s*([^}]+))?\s*\}\}/gi
const PRODUCT_CARDS_TOKEN = /\{\{\s*PRODUCT_CARDS\s*\}\}/gi

function toText(value = "") {
  return String(value || "").trim()
}

function escapeHTML(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function toImageURL(product = {}) {
  const candidates = [
    product?.thumbnail,
    product?.image_url,
    product?.image,
    product?.avatar,
    Array.isArray(product?.images) ? product.images[0] : "",
  ]

  for (const value of candidates) {
    const url = toText(value)
    if (url) {
      return url
    }
  }

  return ""
}

function toPriceText(product = {}) {
  const minPrice = Number(product?.min_price || 0)
  const maxPrice = Number(product?.max_price || 0)

  if (Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice > 0 && maxPrice > 0) {
    if (minPrice === maxPrice) {
      return `${minPrice.toLocaleString("vi-VN")}đ`
    }
    return `${minPrice.toLocaleString("vi-VN")}đ - ${maxPrice.toLocaleString("vi-VN")}đ`
  }

  const salesPrice = Number(product?.sales_price || 0)
  if (Number.isFinite(salesPrice) && salesPrice > 0) {
    return `${salesPrice.toLocaleString("vi-VN")}đ`
  }

  return ""
}

function renderProductCards(products = []) {
  const safeProducts = Array.isArray(products) ? products.filter(Boolean) : []
  if (safeProducts.length === 0) {
    return ""
  }

  const cards = safeProducts.map((product) => {
    const imageURL = toImageURL(product)
    const productID = toText(product?.id)
    const productPath = productID ? `/product/${encodeURIComponent(productID)}` : ""
    const name = escapeHTML(product?.name || "Sản phẩm")
    const price = escapeHTML(toPriceText(product))

    const imageHTML = imageURL
      ? `<img src="${escapeHTML(imageURL)}" alt="${name}" class="block w-full h-full object-cover" loading="lazy" decoding="async" />`
      : '<div class="lp-empty-media">Chưa có ảnh sản phẩm</div>'

    return [
      '<article class="lp-product-card">',
      productPath ? `<a href="${escapeHTML(productPath)}" class="lp-product-link" aria-label="Xem sản phẩm ${name}">` : "",
      `<div class="lp-media-wrap">${imageHTML}</div>`,
      `<h3 class="lp-product-name">${name}</h3>`,
      price ? `<p class="lp-product-price">${price}</p>` : "",
      productPath ? "</a>" : "",
      "</article>",
    ].join("")
  })

  return [
    '<section class="lp-product-list-section">',
    '<div class="lp-product-list-grid">',
    cards.join(""),
    "</div>",
    "</section>",
  ].join("")
}

async function fetchProductsByIDs(ids = []) {
  const uniqueIDs = Array.from(new Set((Array.isArray(ids) ? ids : []).map((id) => toText(id)).filter(Boolean)))
  if (uniqueIDs.length === 0) {
    return []
  }

  const products = await Promise.all(uniqueIDs.map(async (id) => {
    try {
      const data = await productService.get(id)
      return data || null
    } catch {
      return null
    }
  }))

  return products.filter(Boolean)
}

function collectTokenIDs(html = "") {
  const ids = []

  const productMatches = String(html || "").matchAll(/\{\{\s*PRODUCT\s*:\s*([^}]+)\s*\}\}/gi)
  for (const match of productMatches) {
    ids.push(toText(match?.[1]))
  }

  const listMatches = String(html || "").matchAll(/\{\{\s*PRODUCT_LIST\s*:\s*([^}]+)\s*\}\}/gi)
  for (const match of listMatches) {
    const tokens = String(match?.[1] || "")
      .split(",")
      .map((id) => toText(id))
      .filter(Boolean)
    ids.push(...tokens)
  }

  return Array.from(new Set(ids.filter(Boolean)))
}

function replaceProductTokens({ html = "", byID = new Map(), fallbackIDs = [] }) {
  let output = String(html || "")

  const toProducts = (ids = []) => {
    return (Array.isArray(ids) ? ids : [])
      .map((id) => byID.get(toText(id)))
      .filter(Boolean)
  }

  output = output.replace(PRODUCT_TOKEN, (full, idText = "") => {
    const product = byID.get(toText(idText))
    return renderProductCards(product ? [product] : [])
  })

  output = output.replace(PRODUCT_LIST_TOKEN, (full, idsText = "") => {
    const ids = toText(idsText)
      ? String(idsText).split(",").map((id) => toText(id)).filter(Boolean)
      : fallbackIDs

    return renderProductCards(toProducts(ids))
  })

  output = output.replace(PRODUCT_CARDS_TOKEN, () => {
    return renderProductCards(toProducts(fallbackIDs))
  })

  return output
}

export default function useLandingProductContent({ contentHtml = "", productIDs = [] } = {}) {
  const [state, setState] = useState({
    html: String(contentHtml || ""),
    products: [],
  })

  const normalizedProductIDs = useMemo(() => {
    return Array.from(new Set((Array.isArray(productIDs) ? productIDs : []).map((id) => toText(id)).filter(Boolean)))
  }, [productIDs])

  useEffect(() => {
    let active = true

    const run = async () => {
      const rawHtml = String(contentHtml || "")
      if (!rawHtml) {
        if (active) {
          setState({ html: "", products: [] })
        }
        return
      }

      const tokenIDs = collectTokenIDs(rawHtml)
      const fallbackIDs = normalizedProductIDs
      const allIDs = Array.from(new Set([...tokenIDs, ...fallbackIDs]))

      if (allIDs.length === 0) {
        if (active) {
          setState({
            html: rawHtml.replace(PRODUCT_CARDS_TOKEN, ""),
            products: [],
          })
        }
        return
      }

      const products = await fetchProductsByIDs(allIDs)
      if (!active) {
        return
      }

      const byID = new Map(products.map((item) => [toText(item?.id), item]))
      const nextHTML = replaceProductTokens({
        html: rawHtml,
        byID,
        fallbackIDs,
      })

      const endProducts = fallbackIDs
        .map((id) => byID.get(id))
        .filter(Boolean)

      setState({
        html: nextHTML,
        products: endProducts,
      })
    }

    run()

    return () => {
      active = false
    }
  }, [contentHtml, normalizedProductIDs])

  return state
}
